import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { getCurrentAdmin } from '@/services/admin';
import type { Admin } from '@/services/types';
import { isUnauthorizedRequestError } from '@/utils/requestError';
import defaultSettings from '../config/defaultSettings';
import { errorConfig, getToken, removeToken } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: Admin;
  loading?: boolean;
  fetchUserInfo?: () => Promise<Admin | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // 检查是否有 token
      const token = getToken();
      if (!token) {
        return undefined;
      }

      const response = await getCurrentAdmin();
      return response.data;
    } catch (error: any) {
      console.error('获取用户信息失败:', error);
      if (isUnauthorizedRequestError(error)) {
        removeToken();
        history.push(loginPath);
        return undefined;
      }
      throw error;
    }
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: '/images/avatar.png',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const token = getToken();
      // 没有 token 或没有用户信息，重定向到登录页
      if (
        (!token || !initialState?.currentUser) &&
        location.pathname !== loginPath
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: '/images/layout-bg-1.webp',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/images/layout-bg-2.webp',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/images/layout-bg-3.webp',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            onSettingChange={(settings) => {
              setInitialState((preInitialState) => ({
                ...preInitialState,
                settings,
              }));
            }}
          />
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
